// Shared layout-document helpers used by both Layout Builder shells - the public,
// unauthenticated shell (public-layout-builder/PublicLayoutBuilderPage.jsx) and the
// authenticated workspace shell (LayoutBuilderWorkspace.jsx). Centralized so the two
// clients never drift on what counts as a blank/meaningful layout or how a layout UUID
// is decoded out of a route.
import { DEFAULT_LAYOUT_NAME } from './layoutBuilderCopy'

// Blank starting point for a brand-new layout, before the user names it or adds any
// integrations.
export const blankLayout = () => ({ name: DEFAULT_LAYOUT_NAME, integrations: [] })

// True once a layout has diverged from the blank starting point - either renamed away
// from the placeholder or given at least one integration. Used by the public shell to
// gate create-on-first-edit (a layout is only persisted once it's meaningful).
export const isMeaningfulLayout = layout => {
  const name = layout.name?.trim()
  return Boolean(
    (name && name.toLowerCase() !== DEFAULT_LAYOUT_NAME.toLowerCase()) ||
    layout.integrations?.length
  )
}

// Safely decodes a layout UUID captured from a URL path segment, returning null instead
// of throwing on a malformed (invalid percent-escape) segment. Each shell keeps its own
// route regex - this is just the shared "decode the captured segment" step.
export const decodeLayoutUuid = raw => {
  try {
    return decodeURIComponent(raw)
  } catch {
    return null
  }
}

// Same check the backend applies after trimming and lowercasing. DynamoDB keys are
// case-sensitive and stored layout UUIDs are lowercase, so lowercasing is required.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

// Returns the canonical lowercase layout UUID, or null when the value isn't one.
export const normalizeLayoutUuid = raw => {
  if (typeof raw !== 'string') return null
  const candidate = raw.trim().toLowerCase()
  return UUID_PATTERN.test(candidate) ? candidate : null
}

// The portal workspace route for a layout, or for the layouts list when layoutUuid is omitted.
export const workspaceLayoutPath = (companyUUID, layoutUuid) =>
  `/c/${encodeURIComponent(companyUUID)}/layouts${layoutUuid ? `/${encodeURIComponent(layoutUuid)}` : ''}`

// Every integration and flow is assigned a real id at creation time (see
// AddIntegrationDialog's onAdd and blankFlow), so identity for duplicate-named
// records never depends on name or array position (LB-21). This just guards a missing
// `flows` array on a saved integration (an older or hand-edited document).
export const normalizeIntegrations = integrations => (integrations || []).map(integration => ({
  ...integration,
  flows: integration.flows || []
}))

// The layout the implement review reads: every integration has `flows` and `implementations` arrays.
export const normalizeLayoutForReview = layout => ({
  ...layout,
  integrations: normalizeIntegrations(layout.integrations).map(integration => ({
    ...integration,
    implementations: integration.implementations || []
  }))
})
